import React, { useState } from 'react';
import {
  Typography,
  Stack,
  TextField,
  ToggleButton,
  styled,
  Button,
} from '@mui/material';
import type { CapacityValues } from '../../../types/components/filter_bar_types';
import { Icon } from '../../Icon';

interface CapacityFilterPickerProps {
  initialValues: CapacityValues;
  onSubmit?: (values: CapacityValues) => void;
  onChange?: (values: CapacityValues) => void;
  onCancel?: () => void;
}

const StyledToggleButton = styled(ToggleButton)(({ theme }) => ({
  width: 123,
  height: 40,
  borderRadius: 8,
  gap: 8,
  padding: '8px 16px',
  borderWidth: 1,
  border: '1px solid #0000001A',
  '&.Mui-selected': {
    border: '2px solid #000000',
    backgroundColor: '#F5F5F5',
  },
}));

export const CapacityFilterPicker = ({
  initialValues,
  onSubmit,
  onChange,
  onCancel,
}: CapacityFilterPickerProps) => {
  const [minGuests, setMinGuests] = useState(initialValues.minGuests);
  const [maxGuests, setMaxGuests] = useState(initialValues.maxGuests);
  const [allowSeated, setAllowSeated] = useState(initialValues.allowSeated);
  const [allowStanding, setAllowStanding] = useState(
    initialValues.allowStanding,
  );

  const handleSubmit = () => {
    onSubmit && onSubmit({ minGuests, maxGuests, allowSeated, allowStanding });
  };

  // Reset internal state when initialValues change
  React.useEffect(() => {
    setMinGuests(initialValues.minGuests);
    setMaxGuests(initialValues.maxGuests);
    setAllowSeated(initialValues.allowSeated);
    setAllowStanding(initialValues.allowStanding);
  }, [initialValues]);

  React.useEffect(() => {
    onChange && onChange({ minGuests, maxGuests, allowSeated, allowStanding });
  }, [onChange, minGuests, maxGuests, allowSeated, allowStanding]);

  const handleMinGuestsChange = (value: number) => {
    setMinGuests(value);
  };

  const handleMaxGuestsChange = (value: number) => {
    setMaxGuests(value);
  };

  return (
    <Stack
      direction="column"
      gap="20px"
      borderRadius="8px"
      sx={{
        maxWidth: '376px',
      }}
    >
      <Typography variant="subtitle1">How many guests?</Typography>
      <Stack direction="row" gap="20px">
        <TextField
          label="Minimum Guests"
          variant="outlined"
          value={minGuests ?? ''}
          onChange={(e) => handleMinGuestsChange(Number(e.target.value))}
        />
        <TextField
          label="Maximum Guests"
          variant="outlined"
          value={maxGuests ?? ''}
          onChange={(e) => handleMaxGuestsChange(Number(e.target.value))}
        />
      </Stack>
      <Typography variant="subtitle1">Event Style</Typography>
      <Stack direction="row" gap="20px" flexWrap="wrap">
        <StyledToggleButton
          value="seated"
          selected={allowSeated}
          onChange={() => setAllowSeated(!allowSeated)}
        >
          <Icon name="chair" />
          Seated
        </StyledToggleButton>
        <StyledToggleButton
          value="standing"
          selected={allowStanding}
          onChange={() => setAllowStanding(!allowStanding)}
        >
          <Icon name="standing" />
          Standing
        </StyledToggleButton>
      </Stack>
      <Stack direction="row" justifyContent="space-between">
        {onCancel && (
          <Button variant="text" color="secondary" onClick={onCancel}>
            Clear
          </Button>
        )}
        {onSubmit && (
          <Button variant="contained" color="secondary" onClick={handleSubmit}>
            View Results
          </Button>
        )}
      </Stack>
    </Stack>
  );
};
